import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, PenLine, ClipboardList, BookOpen, BarChart3, Wind, RotateCcw, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";
import CheckinPage from "@/pages/CheckinPage";
import WelcomeOverlay from "@/pages/WelcomeOverlay";

export default function DashboardPage() {
  const [journey, setJourney] = useState(null);
  const [showCheckin, setShowCheckin] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [loading, setLoading] = useState(true);
  const [intention, setIntention] = useState(null);
  const [intentionText, setIntentionText] = useState("");
  const [editingIntention, setEditingIntention] = useState(false);
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const fetchJourney = () => {
    API.get("/journey").then(res => {
      setJourney(res.data);
      setShowCheckin(!res.data.has_checkin);
      if (!res.data.has_checkin) {
        setShowWelcome(false);
      } else {
        setShowWelcome(!user?.has_seen_welcome);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchJourney();
    API.get("/intention").then(res => {
      setIntention(res.data);
      setIntentionText(res.data.text || "");
    }).catch(() => {});
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-[#A0655A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (showCheckin) {
    return <CheckinPage onComplete={() => { setShowCheckin(false); fetchJourney(); }} />;
  }

  if (showWelcome) {
    return (
      <WelcomeOverlay
        userName={user?.name}
        onComplete={() => { setShowWelcome(false); refreshUser(); }}
      />
    );
  }

  const completedCount = journey?.completed_layers?.length || 0;
  const progressPercent = Math.round((completedCount / 9) * 100);
  
  let currentLayer = null;
  let currentMovement = null;
  journey?.movements?.forEach(m => {
    m.layers?.forEach(l => {
      if (l.status === 'current' && !currentLayer) {
        currentLayer = l;
        currentMovement = m;
      }
    });
  });

  const JOURNEY_QUOTES = [
    { text: "Du är redan på väg. Att du är här räcker.", stage: "all" },
    { text: "Det finns inget att prestera. Bara att vara.", stage: "all" },
    { text: "Ge dig själv det utrymme du alltid gett andra.", stage: "all" },
    { text: "Ibland är det modigaste man kan göra att stanna upp.", stage: "all" },
    { text: "Det du känner har alltid ett syfte.", stage: "all" },
    { text: "Att se sig själv kräver mod — och du har det.", stage: "1" },
    { text: "Bakom varje mönster finns en del av dig som vill bli sedd.", stage: "1" },
    { text: "Det du bär behöver inte längre definiera dig.", stage: "2" },
    { text: "Att släppa taget börjar med att ge dig själv tillåtelse.", stage: "2" },
    { text: "Du behöver inte bli någon. Du får bara vara.", stage: "3" },
  ];

  const getQuote = () => {
    const mId = currentMovement?.id ? String(currentMovement.id) : "";
    const currentStage = mId.includes('-') ? mId.split('-')[0] : mId.charAt(0) || "all";
    const relevant = JOURNEY_QUOTES.filter(q => q.stage === currentStage || q.stage === "all");
    const dayIndex = new Date().getDate() % relevant.length;
    return relevant[dayIndex];
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 10) return "God morgon";
    if (hour < 17) return "Välkommen";
    return "God kväll";
  };

  const firstName = user?.name?.split(' ')[0] || '';
  const quote = getQuote();

  const progressLabel = completedCount === 0
    ? "Din resa börjar här"
    : completedCount === 9
    ? "Du har gått hela vägen"
    : `${completedCount} av 9 steg avklarade`;

  const saveIntention = () => {
    if (intentionText.trim()) {
      API.post("/intention", { text: intentionText.trim() }).then(res => {
        setIntention(res.data);
        setEditingIntention(false);
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <div className="pt-24 sm:pt-28 pb-8 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          
          {/* Warm greeting */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
            data-testid="journey-header"
          >
            <p className="text-sm uppercase tracking-[0.25em] text-[#A0655A] font-medium mb-4" data-testid="greeting-label">
              {getGreeting()}{firstName ? `, ${firstName}` : ''}
            </p>
            <h1 className="heading-font text-3xl sm:text-4xl font-medium text-[#111111] mb-3">
              Emotionell Frihet
            </h1>
            <p className="text-[#333] text-base max-w-md mx-auto leading-relaxed">
              Din plats att landa, reflektera och växa — i din egen takt.
            </p>
          </motion.div>

          {/* Daily quote */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.8 }}
            className="text-center mb-8 sm:mb-12"
            data-testid="daily-quote"
          >
            <div className="inline-block px-5 sm:px-6 py-3 sm:py-4 rounded-2xl bg-white border border-[#EBE3DF]/50">
              <p className="heading-font text-base sm:text-lg md:text-xl italic text-[#111111]/70 leading-relaxed">
                "{quote.text}"
              </p>
            </div>
          </motion.div>

          {/* Daily intention */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-8 sm:mb-12"
            data-testid="daily-intention"
          >
            {intention?.is_set && !editingIntention ? (
              <div className="text-center">
                <p className="text-xs uppercase tracking-[0.2em] text-[#111111]/40 mb-2">Min intention idag</p>
                <button onClick={() => setEditingIntention(true)} className="inline-block px-5 py-3 rounded-xl bg-white border border-[#EBE3DF]/50 hover:shadow-sm transition-all">
                  <p className="text-sm text-[#111111]/70 italic" data-testid="intention-text">{intention.text}</p>
                </button>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-xs uppercase tracking-[0.2em] text-[#111111]/40 mb-3">
                  {intention?.is_set ? 'Ändra din intention' : 'Sätt en intention för idag'}
                </p>
                <div className="flex gap-2 max-w-sm mx-auto">
                  <input
                    type="text"
                    value={intentionText}
                    onChange={e => setIntentionText(e.target.value)}
                    placeholder="Jag vill..."
                    className="flex-1 px-4 py-2.5 rounded-xl border border-[#EBE3DF] bg-white text-sm focus:outline-none focus:border-[#A0655A] text-[#111111]"
                    data-testid="intention-input"
                    onKeyDown={e => {
                      if (e.key === 'Enter' && intentionText.trim()) saveIntention();
                    }}
                  />
                  <Button
                    onClick={saveIntention}
                    className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-xl px-4"
                    data-testid="save-intention-btn"
                  >
                    Spara
                  </Button>
                </div>
              </div>
            )}
          </motion.div>

          {/* Continue journey card — THE METHOD */}
          {currentLayer && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="mb-4"
            >
              <p className="text-xs uppercase tracking-[0.15em] text-[#111111]/30 font-medium mb-3 ml-1">Metoden — 9 steg</p>
              <button
                onClick={() => navigate(`/steg/${currentLayer.id}`)}
                className="w-full bg-white border border-[#EBE3DF] rounded-2xl p-5 sm:p-7 text-left hover:shadow-lg transition-all group"
                data-testid="continue-journey-btn"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#A0655A] font-medium mb-1.5">
                      {completedCount === 0 ? 'Börja din resa' : 'Fortsätt din resa'}
                    </p>
                    <p className="font-medium text-[#111111] text-base sm:text-lg">{currentLayer.title}</p>
                    <p className="text-xs sm:text-sm text-[#111111]/40 mt-1">{currentMovement?.title} · {currentMovement?.subtitle}</p>
                  </div>
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#A0655A] flex items-center justify-center group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <ChevronRight className="text-white" size={22} />
                  </div>
                </div>
                {/* Mini progress */}
                <div className="mt-4 pt-4 border-t border-[#EBE3DF]/50">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-xs text-[#111111]/30">{progressLabel}</p>
                    {completedCount > 0 && completedCount < 9 && (
                      <p className="text-xs text-[#A0655A]">{completedCount}/9</p>
                    )}
                  </div>
                  <Progress value={progressPercent} className="h-1" />
                </div>
              </button>
            </motion.div>
          )}

          {/* All 9 steps — full navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28 }}
            className="mb-10 sm:mb-14"
          >
            {journey?.movements?.map((m, mIdx) => (
              <div key={m.id} className="mb-4">
                <p className="text-[10px] uppercase tracking-[0.15em] text-[#A0655A]/60 font-medium mb-1.5 ml-1">
                  {m.title} · {m.subtitle}
                </p>
                <div className="space-y-1.5">
                  {m.layers?.map((l) => {
                    const isCompleted = journey.completed_layers?.includes(l.id);
                    const isCurrent = l.id === currentLayer?.id;
                    return (
                      <button
                        key={l.id}
                        onClick={() => navigate(`/steg/${l.id}`)}
                        className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-left transition-all ${
                          isCurrent
                            ? 'bg-[#A0655A]/[0.08] border border-[#A0655A]/20'
                            : isCompleted
                            ? 'bg-[#F5F0EB]/50 border border-transparent hover:border-[#E8E0DB]'
                            : 'bg-white border border-[#E8E0DB]/50 hover:border-[#E8E0DB]'
                        }`}
                        data-testid={`step-nav-${l.id}`}
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium ${
                          isCompleted
                            ? 'bg-[#A0655A] text-white'
                            : isCurrent
                            ? 'bg-[#A0655A]/20 text-[#A0655A]'
                            : 'bg-[#E8E0DB]/50 text-[#999]'
                        }`}>
                          {isCompleted ? <Check size={12} /> : l.order}
                        </div>
                        <span className={`text-sm ${
                          isCurrent ? 'text-[#0A0A0A] font-medium' : isCompleted ? 'text-[#0A0A0A]/70' : 'text-[#0A0A0A]/50'
                        }`}>
                          {l.title}
                        </span>
                        {isCurrent && (
                          <ChevronRight size={14} className="ml-auto text-[#A0655A]" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {completedCount > 0 && (
              <div className="text-center mt-4">
                <button
                  onClick={async () => {
                    if (window.confirm("Vill du verkligen börja om? Dina reflektioner och dagbok sparas, men din progress nollställs.")) {
                      await API.post("/journey/restart");
                      fetchJourney();
                    }
                  }}
                  className="text-xs text-[#111111]/30 hover:text-[#A0655A] transition-colors inline-flex items-center gap-1"
                  data-testid="restart-journey-btn"
                >
                  <RotateCcw size={12} />
                  Börja om från början
                </button>
              </div>
            )}
          </motion.div>

          {/* Tools section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8 sm:mb-12"
          >
            <p className="text-xs uppercase tracking-[0.15em] text-[#111111]/30 font-medium mb-3 ml-1">Dina verktyg</p>
            <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
              <button
                onClick={() => navigate('/dagbok')}
                className="bg-white border border-[#EBE3DF] rounded-2xl p-4 sm:p-5 text-left hover:shadow-md transition-all group min-h-[88px]"
                data-testid="quick-diary"
              >
                <PenLine className="text-[#A0655A] mb-2 sm:mb-3" size={20} />
                <p className="text-sm font-medium text-[#111111] mb-0.5 sm:mb-1">Dagbok</p>
                <p className="text-xs text-[#111111]/40 leading-snug">Skriv fritt om det du bär på</p>
              </button>

              <button
                onClick={() => navigate('/ovningar')}
                className="bg-white border border-[#EBE3DF] rounded-2xl p-4 sm:p-5 text-left hover:shadow-md transition-all group min-h-[88px]"
                data-testid="quick-exercises"
              >
                <Wind className="text-[#A0655A] mb-2 sm:mb-3" size={20} />
                <p className="text-sm font-medium text-[#111111] mb-0.5 sm:mb-1">Övningar</p>
                <p className="text-xs text-[#111111]/40 leading-snug">Stanna upp och känn efter</p>
              </button>

              <button
                onClick={() => navigate('/tester')}
                className="bg-white border border-[#EBE3DF] rounded-2xl p-4 sm:p-5 text-left hover:shadow-md transition-all group min-h-[88px]"
                data-testid="quick-tests"
              >
                <ClipboardList className="text-[#A0655A] mb-2 sm:mb-3" size={20} />
                <p className="text-sm font-medium text-[#111111] mb-0.5 sm:mb-1">Självtester</p>
                <p className="text-xs text-[#111111]/40 leading-snug">Utforska var du befinner dig</p>
              </button>

              <button
                onClick={() => navigate('/progress')}
                className="bg-white border border-[#EBE3DF] rounded-2xl p-4 sm:p-5 text-left hover:shadow-md transition-all group min-h-[88px]"
                data-testid="quick-journal"
              >
                <BookOpen className="text-[#A0655A] mb-2 sm:mb-3" size={20} />
                <p className="text-sm font-medium text-[#111111] mb-0.5 sm:mb-1">Min resa</p>
                <p className="text-xs text-[#111111]/40 leading-snug">Reflektioner och progress</p>
              </button>
            </div>
          </motion.div>

          {/* Journey overview — collapsed, expandable */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
          >
            <button
              onClick={() => navigate('/progress')}
              className="w-full bg-[#F9F3EF]/50 border border-[#EBE3DF]/50 rounded-2xl p-5 text-left hover:bg-[#F9F3EF] transition-all"
              data-testid="view-progress-btn"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <BarChart3 className="text-[#A0655A]" size={20} />
                  <div>
                    <p className="text-sm font-medium text-[#111111]">Se hela din progress</p>
                    <p className="text-xs text-[#111111]/40">{completedCount} av 9 steg · {progressPercent}% avklarat</p>
                  </div>
                </div>
                <ChevronRight className="text-[#A0655A]/50" size={18} />
              </div>
            </button>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
